
import React, { createContext, useContext, useState, useEffect, useMemo, useRef } from 'react';
import axios from '../api/axiosInstance';
import {
  fetchRelationships,
  fetchRelationshipNetwork,
  fetchSessionRelationshipContext,
} from '../utils/memory/mongoMemoryUtils';
import { debugLog } from '../utils/logging/debugLog';
import { buildRelationshipEdgeIdentityKey } from '../utils/relationships/relationshipOntology';

const ChatContext = createContext();

function safeParseLocalStorage(key, fallback) {
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : fallback;
  } catch (err) {
    console.warn(`⚠️ Failed to parse localStorage key "${key}":`, err.message);
    return fallback;
  }
}

function normalizeString(value = '') {
  return String(value).trim().toLowerCase();
}


function buildRelationshipStateKey(record = {}) {
  const firstName = normalizeString(record?.first_name || record?.firstName || '');
  const relationship = normalizeString(record?.relationship || record?.role || '');

  if (!firstName || !relationship) return '';
  return `${firstName}::${relationship}`;
}

function buildActiveIdentity({
  user = null,
  memory = {},
  relationships = [],
  relationshipNetwork = { edges: [] },
  hydratedFromServer = false,
  hydrateFailed = false,
  localUserSnapshot = null,
}) {
  const accountId = user?.id || user?.userId || null;
  const displayName = user?.first_name || memory?.userName || 'Friend';
  const shadowId = user?.shadowId || user?.shadow_id || null;

  const localAccountId = localUserSnapshot?.id || localUserSnapshot?.userId || null;
  const localDisplayName = localUserSnapshot?.first_name || null;
  const localEmail = localUserSnapshot?.email || null;

  const serverDisplayName = user?.first_name || null;
  const serverEmail = user?.email || null;

  const relationshipEdges = Array.isArray(relationshipNetwork?.edges)
    ? relationshipNetwork.edges
    : [];

  const accountIdPresent = !!accountId;
  const displayNamePresent = !!(displayName && displayName !== 'Friend');
  const relationshipsLoaded = Array.isArray(relationships);
  const relationshipsCount = relationships.length;
  const relationshipNetworkLoaded = Array.isArray(relationshipEdges);
  const relationshipNetworkEdgeCount = relationshipEdges.length;

  const idConsistent =
    !localAccountId || !accountId ? true : String(localAccountId) === String(accountId);

  const displayNameConsistent =
    !localDisplayName || !serverDisplayName
      ? true
      : normalizeString(localDisplayName) === normalizeString(serverDisplayName);

  const emailConsistent =
    !localEmail || !serverEmail
      ? true
      : normalizeString(localEmail) === normalizeString(serverEmail);

  const sourceConsistency = idConsistent && displayNameConsistent && emailConsistent;

  const signals = {
    accountIdPresent,
    displayNamePresent,
    hydratedFromServer,
    hydrateFailed,
    sourceConsistency,
    relationshipsLoaded,
    relationshipsCount,
    relationshipNetworkLoaded,
    relationshipNetworkEdgeCount,
    shadowIdPresent: !!shadowId,
  };

  let confidence = 0;
  const reasons = [];

  if (accountIdPresent) {
    confidence += 0.45;
    reasons.push('accountId present');
  } else {
    reasons.push('accountId missing');
  }

  if (displayNamePresent) {
    confidence += 0.2;
    reasons.push('displayName present');
  } else {
    reasons.push('displayName fallback only');
  }

  if (hydratedFromServer) {
    confidence += 0.2;
    reasons.push('hydrated from server');
  } else if (user) {
    confidence += 0.05;
    reasons.push('using local user snapshot');
  } else {
    reasons.push('not hydrated from server');
  }

  if (sourceConsistency) {
    confidence += 0.1;
    reasons.push('source consistency confirmed');
  } else {
    confidence -= 0.2;
    reasons.push('source consistency mismatch');
  }

  if (relationshipsLoaded) {
    confidence += 0.05;
    reasons.push(
      relationshipsCount > 0
        ? `relationships loaded (${relationshipsCount})`
        : 'relationships loaded (empty)'
    );
  }

  if (relationshipNetworkLoaded && relationshipNetworkEdgeCount > 0) {
    reasons.push(`relationship network loaded (${relationshipNetworkEdgeCount})`);
  }

  if (hydrateFailed && !hydratedFromServer) {
    confidence -= 0.1;
    reasons.push('recent hydration failure');
  }

  confidence = Math.max(0, Math.min(1, Number(confidence.toFixed(2))));

  let status = 'unknown';

  if (confidence >= 0.9) {
    status = 'anchored';
  } else if (confidence >= 0.7) {
    status = 'stable';
  } else if (confidence >= 0.45) {
    status = 'partial';
  } else {
    status = 'weak';
  }

  const reason = reasons.join(' | ');

  return {
    accountId,
    displayName,
    shadowId,
    shadowIdPresent: !!shadowId,
    relationships,
    relationshipNetwork,
    confidence,
    status,
    signals,
    reason,
    hydrationSource: hydratedFromServer ? 'server' : user ? 'local' : 'none',
  };
}

export const ChatProvider = ({ children }) => {
  const [memory, setMemory] = useState(() => safeParseLocalStorage('daisy-memory', {}));

  const [user, setUser] = useState(() => safeParseLocalStorage('daisy-user', null));

  const [relationships, setRelationships] = useState(() =>
    safeParseLocalStorage('daisy-relationships', [])
  );

  const [relationshipNetwork, setRelationshipNetwork] = useState(() =>
    safeParseLocalStorage('daisy-relationship-network', { edges: [] })
  );

  const [loading, setLoading] = useState(true);
  const [hydratedFromServer, setHydratedFromServer] = useState(false);
  const [hydrateFailed, setHydrateFailed] = useState(false);
  const relationshipHydrationUserRef = useRef(null);

  const localUserSnapshot = useMemo(() => safeParseLocalStorage('daisy-user', null), [user?.id]);

  const refreshRelationships = async () => {
    if (!user) {
      setRelationships([]);
      setRelationshipNetwork({ edges: [] });
      localStorage.removeItem('daisy-relationships');
      localStorage.removeItem('daisy-relationship-network');
      return [];
    }

    try {
      const fetchedRelationships = await fetchRelationships();

      const safeRelationships = Array.isArray(fetchedRelationships)
        ? fetchedRelationships
        : [];

      setRelationships(safeRelationships);
      localStorage.setItem('daisy-relationships', JSON.stringify(safeRelationships));

      debugLog('🧑‍🤝‍🧑 ChatContext relationships loaded:', safeRelationships);

      safeRelationships.forEach((person) => {
        debugLog('🧠 [ChatContext Relationship State]', {
          first_name: person?.first_name || null,
          last_name: person?.last_name || null,
          relationship: person?.relationship || null,
          lifeState: person?.lifeState || 'unknown',
          healthState: person?.healthState || 'unknown',
          location: person?.location || null,
          source: person?.source || null,
          isActive: person?.isActive,
          updatedAt: person?.updatedAt || null,
        });
      });

      return safeRelationships;
    } catch (err) {
      console.warn(
        '⚠️ ChatContext failed to load relationships:',
        err?.response?.data || err.message
      );
      setRelationships([]);
      localStorage.removeItem('daisy-relationships');
      return [];
    }
  };

  // Step 08 relationship graph: hydrate once, compare locally, patch after governed upsert.


  const refreshRelationshipNetwork = async () => {
    if (!user) {
      setRelationshipNetwork({ edges: [] });
      localStorage.removeItem('daisy-relationship-network');
      return { edges: [] };
    }

    try {
      const fetchedRelationshipNetwork = await fetchRelationshipNetwork();

      const safeRelationshipNetwork = {
        edges: Array.isArray(fetchedRelationshipNetwork?.edges)
          ? fetchedRelationshipNetwork.edges
          : [],
      };

      setRelationshipNetwork(safeRelationshipNetwork);
      localStorage.setItem(
        'daisy-relationship-network',
        JSON.stringify(safeRelationshipNetwork)
      );

      debugLog('🕸️ ChatContext relationship network refreshed:', safeRelationshipNetwork);

      return safeRelationshipNetwork;
    } catch (err) {
      console.warn(
        '⚠️ ChatContext failed to refresh relationship network:',
        err?.response?.data || err.message
      );
      setRelationshipNetwork({ edges: [] });
      localStorage.removeItem('daisy-relationship-network');
      return { edges: [] };
    }
  };



  const hydrateRelationshipContext = async () => {
    if (!user) {
      setRelationships([]);
      setRelationshipNetwork({ edges: [] });
      localStorage.removeItem('daisy-relationships');
      localStorage.removeItem('daisy-relationship-network');
      return {
        relationships: [],
        relationshipNetwork: { edges: [] },
      };
    }

    const sessionContext = await fetchSessionRelationshipContext();

    if (!sessionContext) {
      throw new Error('Failed to hydrate relationship session context');
    }

    const safeRelationships = Array.isArray(sessionContext.relationships)
      ? sessionContext.relationships
      : [];

    const safeRelationshipNetwork = {
      edges: Array.isArray(sessionContext.relationshipNetwork?.edges)
        ? sessionContext.relationshipNetwork.edges
        : [],
    };

    setRelationships(safeRelationships);
    setRelationshipNetwork(safeRelationshipNetwork);

    localStorage.setItem('daisy-relationships', JSON.stringify(safeRelationships));
    localStorage.setItem(
      'daisy-relationship-network',
      JSON.stringify(safeRelationshipNetwork)
    );

    debugLog('🧭 ChatContext relationship session hydrated:', {
      relationshipCount: safeRelationships.length,
      relationshipEdgeCount: safeRelationshipNetwork.edges.length,
    });

    return {
      relationships: safeRelationships,
      relationshipNetwork: safeRelationshipNetwork,
    };
  };

  const patchRelationshipStates = (records = []) => {
    const incomingRecords = Array.isArray(records) ? records : [records];
    const validIncomingRecords = incomingRecords.filter((record) =>
      Boolean(buildRelationshipStateKey(record))
    );

    if (!validIncomingRecords.length) {
      return relationships;
    }

    const byKey = new Map();

    relationships.forEach((record) => {
      const stateKey = buildRelationshipStateKey(record);
      if (stateKey && record?.isActive !== false) {
        byKey.set(stateKey, record);
      }
    });

    validIncomingRecords.forEach((record) => {
      const stateKey = buildRelationshipStateKey(record);
      if (!stateKey) return;

      if (record?.isActive === false) {
        byKey.delete(stateKey);
      } else {
        byKey.set(stateKey, record);
      }
    });

    const nextRelationships = Array.from(byKey.values()).sort((a, b) => {
      const relationshipCompare = normalizeString(a?.relationship).localeCompare(
        normalizeString(b?.relationship)
      );

      if (relationshipCompare !== 0) return relationshipCompare;

      return normalizeString(a?.first_name).localeCompare(
        normalizeString(b?.first_name)
      );
    });

    setRelationships(nextRelationships);
    localStorage.setItem('daisy-relationships', JSON.stringify(nextRelationships));

    debugLog('🧑‍🤝‍🧑 ChatContext relationship states patched locally:', {
      patchedRecords: validIncomingRecords,
      relationshipCount: nextRelationships.length,
    });

    return nextRelationships;
  };

  const patchRelationshipNetworkEdges = (edges = []) => {
    const incomingEdges = Array.isArray(edges) ? edges : [edges];
    const validIncomingEdges = incomingEdges.filter((edge) =>
      Boolean(buildRelationshipEdgeIdentityKey(edge))
    );

    if (!validIncomingEdges.length) {
      return relationshipNetwork;
    }

    const byKey = new Map();
    const previousEdges = Array.isArray(relationshipNetwork?.edges)
      ? relationshipNetwork.edges
      : [];

    previousEdges.forEach((edge) => {
      const edgeKey = buildRelationshipEdgeIdentityKey(edge);
      if (edgeKey && edge?.isActive !== false) {
        byKey.set(edgeKey, edge);
      }
    });

    validIncomingEdges.forEach((edge) => {
      const edgeKey = buildRelationshipEdgeIdentityKey(edge);
      if (!edgeKey) return;

      if (edge?.isActive === false) {
        byKey.delete(edgeKey);
      } else {
        byKey.set(edgeKey, edge);
      }
    });

    const nextNetwork = { edges: Array.from(byKey.values()) };

    setRelationshipNetwork(nextNetwork);
    localStorage.setItem(
      'daisy-relationship-network',
      JSON.stringify(nextNetwork)
    );

    debugLog('🕸️ ChatContext relationship network patched locally:', {
      patchedEdges: validIncomingEdges,
      edgeCount: nextNetwork.edges.length,
    });

    return nextNetwork;
  };

  useEffect(() => {
    const hydrateUser = async () => {
      const isOAuthReturn = window.location.search.includes('oauth=success');

      const tryHydrate = async () => {
        const res = await axios.get('/api/auth/me');
        const hydratedUser = res.data;

        setUser(hydratedUser);
        setHydratedFromServer(true);
        setHydrateFailed(false);

        localStorage.setItem('daisy-user', JSON.stringify(hydratedUser));
        debugLog('✅ ChatContext hydrated user from /api/auth/me:', hydratedUser);
      };

      try {
        await tryHydrate();
      } catch (err) {
        if (isOAuthReturn) {
          const delays = [300, 700, 1200];

          for (const delay of delays) {
            try {
              await new Promise((r) => setTimeout(r, delay));
              await tryHydrate();
              setLoading(false);

              const url = new URL(window.location.href);
              url.searchParams.delete('oauth');
              window.history.replaceState({}, '', url.pathname + url.search);

              return;
            } catch (retryErr) {
              console.warn(`⚠️ OAuth retry failed after ${delay}ms`);
            }
          }
        }

        console.warn('⚠️ ChatContext could not hydrate user from /api/auth/me');
        setHydratedFromServer(false);
        setHydrateFailed(true);
        localStorage.removeItem('daisy-user');
        localStorage.removeItem('daisy-relationships');
        localStorage.removeItem('daisy-relationship-network');
        setUser(null);
        setRelationships([]);
        setRelationshipNetwork({ edges: [] });
      } finally {
        setLoading(false);
      }
    };

    hydrateUser();
  }, []);

  useEffect(() => {
    const userId = user?.id || user?.userId || null;

    if (!userId || loading) {
      if (!userId) {
        relationshipHydrationUserRef.current = null;
        setRelationships([]);
        setRelationshipNetwork({ edges: [] });
        localStorage.removeItem('daisy-relationships');
        localStorage.removeItem('daisy-relationship-network');
      }
      return;
    }

    if (relationshipHydrationUserRef.current === String(userId)) {
      return;
    }

    relationshipHydrationUserRef.current = String(userId);

    hydrateRelationshipContext().catch((err) => {
      relationshipHydrationUserRef.current = null;
      console.warn(
        '⚠️ ChatContext failed to hydrate relationship session context:',
        err.message
      );
    });
  }, [user?.id, user?.userId, loading]);

  const activeIdentity = useMemo(() => {
    const identity = buildActiveIdentity({
      user,
      memory,
      relationships,
      relationshipNetwork,
      hydratedFromServer,
      hydrateFailed,
      localUserSnapshot,
    });

    debugLog('🪪 ChatContext activeIdentity:', identity);
    return identity;
  }, [
    user,
    memory?.userName,
    relationships,
    relationshipNetwork,
    hydratedFromServer,
    hydrateFailed,
    localUserSnapshot,
  ]);

  const updateMemory = (key, value) => {
    setMemory((prev) => {
      const updated = { ...prev, [key]: value };
      localStorage.setItem('daisy-memory', JSON.stringify(updated));
      return updated;
    });
  };

  const updateUser = (newUser) => {
    relationshipHydrationUserRef.current = null;
    setUser(newUser);
    setHydratedFromServer(false);
    localStorage.setItem('daisy-user', JSON.stringify(newUser));
  };

  const resetUser = () => {
    localStorage.removeItem('daisy-user');
    localStorage.removeItem('daisy-relationships');
    localStorage.removeItem('daisy-relationship-network');
    setUser(null);
    setRelationships([]);
    setRelationshipNetwork({ edges: [] });
    setHydratedFromServer(false);
    setHydrateFailed(false);
    relationshipHydrationUserRef.current = null;
  };

  return (
    <ChatContext.Provider
      value={{
        memory,
        updateMemory,
        user,
        updateUser,
        resetUser,
        loading,
        relationships,
        setRelationships,
        refreshRelationships,
        patchRelationshipStates,
        relationshipNetwork,
        setRelationshipNetwork,
        refreshRelationshipNetwork,
        patchRelationshipNetworkEdges,
        activeIdentity,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export const useChat = () => useContext(ChatContext);